// 통합 문서 작성 — 조각 업로드(이어 올리기).
//
// 8 MB 조각으로 나눠 차례로 올린다. 신호가 끊기면 잠깐 기다렸다가 서버가 실제로 받은 위치를
// 물어 그 뒤부터 다시 보낸다. 같은 파일을 나중에 다시 올려도(앱을 껐다 켜도) 기억해 둔
// 업로드 id 로 이어 올린다. 진행률·속도·남은 시간을 알려 준다.
//
// 파일 출처는 두 가지다.
//   · 브라우저/앱에서 고른 File         → file.slice()
//   · 안드로이드 앱이 받은 파일(다른 앱에서 열기) → /__native_import/<n>?offset=&length=
//     앱이 그 부분만 읽어 준다 — 큰 영상도 통째로 메모리에 올리지 않는다.
import { api } from "../../shared/api/client";

export type UploadSource = {
  name: string;
  size: number;
  key: string;
  mime?: string;
  read: (start: number, end: number) => Promise<Blob>;
};

export type UploadProgress = {
  loaded: number;
  total: number;
  speed: number;        // B/s (최근 몇 초 평균)
  eta: number | null;   // 초
  state: "uploading" | "retrying" | "finishing";
  retryIn?: number;
};

export const fileSource = (f: File): UploadSource => ({
  name: f.name, size: f.size, key: `f|${f.name}|${f.size}|${f.lastModified}`, mime: f.type || undefined,
  read: async (s, e) => f.slice(s, e),
});

export const nativeSource = (x: { name: string; size: number; url: string }): UploadSource => ({
  name: x.name, size: x.size, key: `n|${x.name}|${x.size}`,
  read: async (s, e) => {
    const r = await fetch(`${x.url}?offset=${s}&length=${e - s}`);
    if (!r.ok) throw new Error(`앱이 파일을 읽지 못했습니다 (${r.status})`);
    return r.blob();
  },
});

const RESUME_KEY = "docstudio.uploads";
const DEFAULT_BASE = "/docstudio/uploads";
// 다른 곳(메일)에서 올리는 같은 파일이 문서 작성의 업로드 id 를 집어 가지 않게 주소별로 따로 기억한다
const resumeKey = (base: string, src: UploadSource) => (base === DEFAULT_BASE ? src.key : `${base}|${src.key}`);
const STALL_MS = 30_000;
function resumeMap(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(RESUME_KEY) || "{}"); } catch { return {}; }
}
function remember(key: string, id: string | null) {
  try {
    const m = resumeMap();
    if (id) m[key] = id; else delete m[key];
    localStorage.setItem(RESUME_KEY, JSON.stringify(m));
  } catch { /* 저장 못 해도 올리기는 된다 */ }
}

const sleep = (ms: number, signal?: AbortSignal) => new Promise<void>((res, rej) => {
  const t = setTimeout(res, ms);
  signal?.addEventListener("abort", () => { clearTimeout(t); rej(new DOMException("취소", "AbortError")); }, { once: true });
});

function status(e: any): number | undefined { return e?.response?.status; }

export async function chunkedUpload<T = any>(
  src: UploadSource,
  // base: 같은 방식의 조각 업로드를 받는 다른 서버 주소(메일 첨부는 /mail/attachments/uploads)
  opts: { app?: string; base?: string; signal?: AbortSignal; onProgress?: (p: UploadProgress) => void },
): Promise<T> {
  const { signal, onProgress } = opts;
  // 이름을 base 로 두면 아래 반복문의 `const base = offset` 에 가려져 조각 주소가 /0/<id> 가 된다
  const endpoint = opts.base ?? DEFAULT_BASE;
  const rkey = resumeKey(endpoint, src);
  let uploadId: string | null = resumeMap()[rkey] ?? null;
  let offset = 0;
  let chunk = 8 * 1024 * 1024;

  if (uploadId) {
    try {
      const st = (await api.get(`${endpoint}/${uploadId}`, { signal })).data;
      offset = st.offset; chunk = st.chunk || chunk;
    } catch { uploadId = null; }
  }
  if (!uploadId) {
    const init = (await api.post(endpoint, { name: src.name, size: src.size, app: opts.app, mime: src.mime }, { signal })).data;
    uploadId = init.upload_id as string; offset = 0; chunk = init.chunk || chunk;
    remember(rkey, uploadId);
  }

  // 속도: 최근 8초 동안 보낸 양으로
  const samples: { t: number; b: number }[] = [];
  const report = (sent: number, state: UploadProgress["state"], retryIn?: number) => {
    const now = performance.now();
    samples.push({ t: now, b: sent });
    while (samples.length > 2 && now - samples[0].t > 8000) samples.shift();
    const first = samples[0];
    const speed = now > first.t ? ((sent - first.b) * 1000) / (now - first.t) : 0;
    onProgress?.({ loaded: sent, total: src.size, speed, eta: speed > 0 ? (src.size - sent) / speed : null, state, retryIn });
  };
  report(offset, "uploading");

  let failures = 0;
  // 미리 읽기: 지금 조각을 보내는 동안 다음 조각을 읽어 둔다(앱이 넘겨주는 파일은 읽기에도 시간이 든다)
  let ahead: { start: number; end: number; blob: Promise<Blob> } | null = null;
  const readPiece = (a: number, b: number) => {
    if (ahead && ahead.start === a && ahead.end === b) { const p = ahead.blob; ahead = null; return p; }
    ahead = null;
    return src.read(a, b);
  };
  while (offset < src.size) {
    const end = Math.min(offset + chunk, src.size);
    // 멈춤 감시: 30초 동안 한 바이트도 못 보내면 그 요청을 버리고 다시 시도한다.
    // 신호가 끊겼다 돌아오면 묵은 연결이 응답 없이 매달려 영영 기다리는 일이 생긴다.
    const piece = new AbortController();
    const onOuterAbort = () => piece.abort();
    signal?.addEventListener("abort", onOuterAbort, { once: true });
    let watchdog: ReturnType<typeof setTimeout> | undefined;
    const kick = () => { clearTimeout(watchdog); watchdog = setTimeout(() => piece.abort(), STALL_MS); };
    try {
      kick();
      const body = await readPiece(offset, end);
      const base = offset;
      kick();
      if (end < src.size) {
        const nEnd = Math.min(end + chunk, src.size);
        const pr = src.read(end, nEnd);
        pr.catch(() => { /* 실패하면 그 차례에 다시 읽는다 */ });
        ahead = { start: end, end: nEnd, blob: pr };
      }
      const r = await api.put(`${endpoint}/${uploadId}`, body, {
        params: { offset }, signal: piece.signal, timeout: 0,
        headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: ev => { kick(); report(base + (ev.loaded || 0), "uploading"); },
      });
      offset = r.data.offset;
      failures = 0;
      report(offset, "uploading");
    } catch (e: any) {
      if (signal?.aborted) throw e;  // 사용자가 멈춤 — 멈춤 감시로 끊은 것은 아래에서 다시 시도
      const s = status(e);
      if (s === 413 || s === 400 || s === 401 || s === 403) throw e;
      if (s === 404) { remember(rkey, null); throw e; }  // 만료 — 처음부터 다시
      // 409(위치 어긋남)·끊김·5xx: 잠깐 쉬고, 서버가 받은 위치에서 다시
      failures += 1;
      if (failures > 30) throw e;
      const wait = s === 409 ? 0 : Math.min(30, 2 ** Math.min(failures, 5));
      for (let left = wait; left > 0; left--) { report(offset, "retrying", left); await sleep(1000, signal); }
      try { offset = (await api.get(`${endpoint}/${uploadId}`, { signal, timeout: 15000 })).data.offset; } catch { /* 다음 차례에 다시 */ }
    } finally {
      clearTimeout(watchdog);
      signal?.removeEventListener("abort", onOuterAbort);
    }
  }
  report(src.size, "finishing");
  for (let i = 0; ; i++) {
    try {
      const item = (await api.post(`${endpoint}/${uploadId}/complete`, null, { signal })).data;
      remember(rkey, null);
      return item as T;
    } catch (e: any) {
      if (signal?.aborted || i >= 5 || status(e) === 409) throw e;
      await sleep(2000, signal);
    }
  }
}

export async function cancelUpload(src: UploadSource, base = DEFAULT_BASE) {
  const rkey = resumeKey(base, src);
  const id = resumeMap()[rkey];
  remember(rkey, null);
  if (id) { try { await api.delete(`${base}/${id}`); } catch { /* 이미 없음 */ } }
}

export function fmtSpeed(bps: number) {
  if (!bps) return "-";
  return bps >= 1024 * 1024 ? `${(bps / 1024 / 1024).toFixed(1)} MB/s` : `${Math.round(bps / 1024)} KB/s`;
}
export function fmtEta(s: number | null) {
  if (s == null || !isFinite(s)) return "계산 중";
  if (s < 60) return `${Math.ceil(s)}초`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}분 ${Math.round(s % 60)}초` : `${Math.floor(m / 60)}시간 ${m % 60}분`;
}
